import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useTopic = (identifier: string) => {
  return useQuery({
    queryKey: ['topic', identifier],
    queryFn: async (): Promise<any> => {
      // Check if identifier is a UUID (legacy) or a slug
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);

      let query = supabase
        .from('topics')
        .select(`
          *,
          categories (name, color, slug, parent_category_id)
        `);

      query = isUUID ? query.eq('id', identifier) : query.eq('slug', identifier);

      const { data: topicData, error } = await query.maybeSingle();
      if (error) throw error;
      if (!topicData) throw new Error('Topic not found');

      // Only fetch author profile when authored (anonymous topics skip this).
      let authorInfo: { profiles?: { display_name: string; avatar_url: string | null } } = {};
      if (topicData.user_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name, avatar_url')
          .eq('id', topicData.user_id)
          .maybeSingle();
        if (profile) authorInfo = { profiles: profile };
      }

      // Use cached reply_count instead of a dedicated COUNT(*) query.
      return {
        ...topicData,
        ...authorInfo,
        postsCount: topicData.reply_count ?? 0,
      };
    },
    enabled: !!identifier,
    staleTime: 30_000,
  });
};

export const useIncrementTopicViews = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (topicId: string) => {
      // Get current view count and increment
      const { data: topic } = await supabase
        .from('topics')
        .select('view_count')
        .eq('id', topicId)
        .single();

      if (topic) {
        const { error } = await supabase
          .from('topics')
          .update({ view_count: (topic.view_count || 0) + 1 })
          .eq('id', topicId);

        if (error) throw error;
      }
    },
    onSuccess: (_, topicId) => {
      queryClient.invalidateQueries({ queryKey: ['topic', topicId] });
    },
  });
};
